import { Flex, FlexCenter, FlexColumn, SmallBody } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import { getDaysOfWeek } from '@ubeya/shared/utils/time';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useField } from 'react-final-form';
import styled from 'styled-components';

const DaysRow = styled(FlexColumn)`
  gap: 4px;
`;

const DaysContainer = styled(Flex)`
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  flex-wrap: wrap;
`;

const Day = styled(FlexCenter)`
  border: 1px solid ${({ theme }) => theme.colors.grey2};
  cursor: ${({ isDisabled }) => (isDisabled ? 'no-drop' : 'pointer')};
  border-radius: 4px;
  background-color: ${({ theme, selected, isDisabled }) =>
    selected ? theme.colors.primary : isDisabled ? theme.colors.grey4 : theme.colors.white};
  flex: 1;
  min-width: 70px;
  height: 64px;
`;

const DayTitle = styled.div`
  font-size: 14px;
  letter-spacing: 1px;
  padding: 5px 2px;
  color: ${({ theme, selected }) => (selected ? theme.colors.white : theme.colors.black)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const WeekDayTitle = styled.div`
  margin-bottom: 6px;
  font-weight: 500;
`;

const FieldLabel = styled(SmallBody)`
  width: 150px;
  color: ${({ theme }) => theme.colors.black};
`;

const DateTitle = styled.div`
  font-size: 12px;
  font-weight: 400;
`;

const HourlyCopyDay = ({ startDate, endDate, branchId, selectedDays, setSelectedDays }) => {
  const { t, shortDateMonthFormat } = useContext(ConfigContext);
  const { mappedBranches } = useBranches();

  const copyRef = useRef(null);

  const daysOptions = useMemo(() => {
    const start = moment(startDate).format(API_DATE_FORMAT);
    const end = moment(endDate).format(API_DATE_FORMAT);

    const daysOfWeek = getDaysOfWeek(start, moment(end).diff(moment(start), 'days') + 1);
    const branchWorkDays = mappedBranches[branchId]?.timesheetConfig?.workDays;
    if (!branchWorkDays?.length) {
      return daysOfWeek;
    }

    return daysOfWeek.filter(({ index }) => branchWorkDays.includes(index));
  }, [startDate, endDate, mappedBranches, branchId]);

  const {
    input: { value: dateValue }
  } = useField('date');

  const onDayClick = useCallback(
    ({ isDisabled, date }) =>
      isDisabled
        ? undefined
        : setSelectedDays((prev) => ({
            ...prev,
            [date]: !prev[date]
          })),
    [setSelectedDays]
  );

  useEffect(() => {
    copyRef && copyRef.current && copyRef.current.scrollIntoView();
  }, []);

  useEffect(() => {
    setSelectedDays(
      daysOptions.reduce(
        (carry, item) => ({
          ...carry,
          [item.date]: item.momentDate.isSame(moment(dateValue), 'date')
        }),
        {}
      )
    );
  }, [dateValue, daysOptions, setSelectedDays]);

  return (
    <DaysRow>
      <FieldLabel>{t('copyThisShift')}</FieldLabel>
      <DaysContainer ref={copyRef}>
        {daysOptions.map(({ momentDate, date, day }, idx) => {
          const isDisabled = momentDate.isSame(moment(dateValue, API_DATE_FORMAT), 'date');

          return (
            <Day
              key={idx}
              selected={selectedDays[date]}
              isDisabled={isDisabled}
              onClick={() => onDayClick({ isDisabled, date })}>
              <DayTitle selected={selectedDays[date]}>
                <WeekDayTitle>{day.toUpperCase()}</WeekDayTitle>
                <DateTitle>{momentDate.format(shortDateMonthFormat)}</DateTitle>
              </DayTitle>
            </Day>
          );
        })}
      </DaysContainer>
    </DaysRow>
  );
};

export default HourlyCopyDay;
